/**
 * Bundle 75 Task 1 - Send-narratives modal.
 *
 * Wraps the morning-of email flow. Fetches the preview payload from
 * `/api/admin/events/{id}/seating/send-narratives/preview`, renders
 * per-table breakdown + a sample email + two send buttons.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Send, TestTube, AlertTriangle, Mail } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const SendNarrativesModal = ({ open, onOpenChange, eventId, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  useEffect(() => {
    if (!open || !eventId) return;
    setBusy(true);
    setPreview(null);
    setConfirmSend(false);
    api.get(`/admin/events/${eventId}/seating/send-narratives/preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Preview failed');
        onOpenChange(false);
      })
      .finally(() => setBusy(false));
  }, [open, eventId, onOpenChange]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/seating/send-narratives/test`,
      );
      if (data.sent) {
        toast.success(`Test sent to your inbox. Status: ${data.results?.[0]?.status || 'sent'}`);
      } else {
        toast.warning(`Test recorded but not sent (status: ${data.results?.[0]?.status || 'unknown'})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const sendAll = async () => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/seating/send-narratives`,
      );
      toast.success(`Dispatched: ${data.sent} sent, ${data.skipped_24h} skipped (24h rate limit), ${data.failed} failed`);
      onSent?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-narratives-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Mail className="h-4 w-4 text-secondary-700" /> Send narratives to attendees
          </DialogTitle>
        </DialogHeader>
        {busy ? (
          <div className="text-sm text-neutral-500 py-8 text-center">Building preview...</div>
        ) : preview ? (
          <div className="space-y-4">
            {!preview.narrative_present ? (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="send-narratives-warn-no-narrative">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  No cached narrative. Recipients will get their tablemate list without the AI narrative.
                  Click &quot;Explain arrangement&quot; first to generate one.
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="bg-primary-900 text-white" data-testid="send-narratives-total">
                {preview.total_recipients} recipients
              </Badge>
              <span className="text-xs text-neutral-600">
                across {preview.per_table.length} tables at {preview.event_title}
              </span>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Per-table breakdown</div>
              <div className="max-h-40 overflow-y-auto border border-neutral-100 rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50">
                    <tr className="text-neutral-500 text-[10px] uppercase tracking-wider">
                      <th className="text-left py-1.5 px-2">Table</th>
                      <th className="text-left py-1.5 px-2">Seated</th>
                      <th className="text-left py-1.5 px-2">Reachable</th>
                      <th className="text-left py-1.5 px-2">Narrative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.per_table.map((t) => (
                      <tr key={t.table_id} className="border-t border-neutral-100" data-testid={`send-narratives-table-${t.table_id}`}>
                        <td className="py-1.5 px-2 font-medium text-primary-900">{t.table_name}</td>
                        <td className="py-1.5 px-2 text-neutral-700">{t.seated_count}</td>
                        <td className="py-1.5 px-2 text-neutral-700">{t.recipient_count}</td>
                        <td className="py-1.5 px-2">
                          {t.has_narrative ? (
                            <span className="text-emerald-700 text-[11px]">yes</span>
                          ) : (
                            <span className="text-amber-700 text-[11px]">missing</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {preview.sample ? (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  Sample email * {preview.sample.attendee_name || preview.sample.email} at {preview.sample.table_name}
                </div>
                <div className="border border-neutral-200 rounded-md overflow-hidden bg-white" data-testid="send-narratives-sample">
                  <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-2 text-xs">
                    <div><span className="text-neutral-500">Subject:</span> <span className="font-medium text-primary-900">{preview.sample.subject}</span></div>
                    <div><span className="text-neutral-500">To:</span> {preview.sample.email}</div>
                  </div>
                  <iframe
                    srcDoc={preview.sample.html}
                    title="Sample rendered email"
                    className="w-full h-72 bg-white"
                    sandbox=""
                    data-testid="send-narratives-sample-iframe"
                  />
                </div>
              </div>
            ) : (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                No seated attendees with an email on file. Seat guests and try again.
              </div>
            )}

            <div className="text-[11px] text-neutral-500 leading-relaxed" data-testid="send-narratives-rate-limit-note">
              Rate limit: one send per attendee per 24 hours. Re-firing after a last-minute seating
              change is allowed; recent recipients are automatically skipped.
            </div>
          </div>
        ) : null}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="send-narratives-cancel">Cancel</Button>
          <Button
            variant="outline" onClick={sendTest}
            disabled={busy || testing || !preview?.sample}
            data-testid="send-narratives-test-btn"
          >
            <TestTube className="h-3.5 w-3.5 mr-1" /> {testing ? 'Sending...' : 'Send test to myself'}
          </Button>
          {!confirmSend ? (
            <Button
              disabled={busy || sending || !preview?.total_recipients}
              onClick={() => setConfirmSend(true)}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="send-narratives-primary-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Send to all seated ({preview?.total_recipients || 0})
            </Button>
          ) : (
            <Button
              disabled={busy || sending || !preview?.total_recipients}
              onClick={sendAll}
              className="bg-red-700 text-white hover:bg-red-800"
              data-testid="send-narratives-confirm-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1" /> {sending ? 'Sending...' : `Confirm send to ${preview?.total_recipients || 0}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendNarrativesModal;
